<template>
  <div
    v-if="transactionStatusPage === TransactionStatus.PENDING"
    class="registration-success-page-container flex-column-reverse-mobile"
  >
    <div class="registration-success-page">
      <h1 class="my-page-title">{{ $t('ticketsReserved') }}</h1>
      <p class="description">
        {{ $t('ticketsReservedDescription.part1') }}
        <strong
          v-if="!isLoadingOrder"
        >{{
           isFreeOrder
             ? $t('ticketsReservedDescription.part2Free')
             : $t('ticketsReservedDescription.part2')
         }}
          {{ customerEmail }}.</strong>
      </p>
      <div
        class="transaction-status-container"
        :style="{ backgroundColor: transactionStatusData.backgroundColor }"
      >
        <spinner v-if="transactionStatus === TransactionStatus.PENDING || isLoadingOrder" />
        <v-icon v-else :color="transactionStatusData.color" size="40">
          {{ transactionStatusData.icon }}
        </v-icon>
        <div>
          <p
            class="transaction-status-title"
            :style="{
              color: transactionStatusData.color,
            }"
          >
            {{ transactionStatusData.title }}
          </p>
          <p
            class="transaction-status-description"
            :style="{
              color: transactionStatusData.color,
            }"
          >
            {{ transactionStatusData.description }}
          </p>
        </div>
      </div>
      <p v-if="!isLoadingOrder" class="description">
        {{
          isFreeOrder
            ? $t('ticketsReservedDescription.part3Free')
            : $t('ticketsReservedDescription.part3')
        }}
      </p>
    </div>
    <img src="/assets/tickets-visual.svg" alt="tickets">
  </div>
  <div
    v-else-if="transactionStatusPage === TransactionStatus.FAILED"
    class="registration-success-page-container flex-column-reverse-mobile"
  >
    <div class="registration-success-page">
      <h1 class="my-page-title">{{ $t('ticketsFailedPayment') }}</h1>
      <p class="description">
        {{ $t('ticketsFailedPaymentDescription.part1')
        }}<strong>{{ $t('ticketsFailedPaymentDescription.part2') }}</strong>{{ $t('ticketsFailedPaymentDescription.part3') }}
      </p>
      <main-button :href="repeatPurchaseUrl" color="black" style="width: 100%; margin-top: 3rem">
        {{ $t('repeatPurchase') }}
      </main-button>
    </div>
    <img src="/assets/tickets-error-visual.svg" alt="tickets error">
  </div>
  <div v-else class="registration-success-page-container">
    <div class="registration-success-page">
      <h1 class="my-page-title">
        {{ !hideTicketsForEvent3360 ? 'Zahtjev uspješno poslan' : $t('ticketsSuccessfulPayment') }}
      </h1>
      <p class="description">
        {{
          !hideTicketsForEvent3360
            ? 'Vaš zahtjev za bankovni transfer je uspješno zaprimljen. Svi detalji transakcije poslani su na vašu e-mail adresu.'
            : $t('ticketsSuccessfulPaymentDescription.part1')
        }}<strong>{{
          isFreeOrder
            ? $t('ticketsSuccessfulPaymentDescription.part2Free')
            : $t('ticketsSuccessfulPaymentDescription.part2')
        }}</strong>
      </p>
      <br>
      <p class="description">
        {{ $t('ticketsSuccessfulPaymentDescription.part3') }}
      </p>

      <div id="buttons-container">
        <a
          v-if="hideTicketsForEvent3360 && user && order"
          target="_blank"
          class="main-button v-btn v-btn--depressed v-btn--round theme--light primary"
          :href="`${customerURL}/orders/${order.uuid}`"
        >
          {{ $t('viewTheOrder') }}
        </a>
        <a
          v-if="hideTicketsForEvent3360"
          id="download-ticket"
          :class="{ 'v-btn--disabled': !ticketDownloaded }"
          class="main-button v-btn v-btn--depressed v-btn--round theme--light download-ticket"
          :disabled="!ticketDownloaded"
        >
          <span>{{ $t('getMyTicket') }}</span>
          <v-progress-circular
            v-show="!ticketDownloaded && !ticketError"
            color="primary"
            indeterminate
          />
        </a>
      </div>
      <button class="button-no-background" @click="goToPublicPage">
        {{ $t('backToEventPage') }}
      </button>
    </div>
    <div v-if="adsLoaded && ads.length" class="shop-section">
      <h4>CoreEvent partner ponuda</h4>
      <div class="shop-cards-section">
        <a
          v-for="ad in ads.slice(0, 3)"
          v-for-key="url"
          class="shop-card"
          target="_blank"
          :href="ad.url"
        >
          <img v-if="ad.logoUrl" class="logo-img" :src="ad.logoUrl">
          <img class="product-img" :src="ad.imageUrl">
          <div class="shop-card-details">
            <h5>{{ ad.title }}</h5>
          </div>
        </a>
      </div>
    </div>
    <div v-else-if="adsLoaded">
      <img src="/assets/successful-payment.svg">
    </div>
  </div>
</template>

<script>
import { getEventAds, getEventSettings, getOrderInfoByUUID, getPaymentStatusToken } from '@/http'
import axios from 'axios'
import { GET_LOGGED_USER } from '@/graphql/user.graphql'
import { CHECK_ORDER_WS_PAY_STATUS } from '@/graphql/order.graphql'
import { customerURL, frontendURL, landingURL } from '@/utils'
import i18next from 'i18next'
import { attachQueryParamsToUrl } from '@/services/attach-query-params-to-url'
import { sendFacebookPixelEvent, sendOrganizationFacebookPixelEvent } from '@/services/fbmeta.service'
import { TransactionStatus } from '@/types'

export default {
  name: 'successful-payment',
  components: {},
  data() {
    return {
      slug: undefined,
      ticketDownloaded: false,
      ticketError: false,
      customerURL,
      landingURL,
      hideTicketsForEvent3360: false,
      user: null,
      order: null,
      ads: [],
      adsLoaded: false,
      organizationMetaPixelId: null,
      transactionStatusPage: TransactionStatus.PENDING,
      transactionStatus: TransactionStatus.PENDING,
      pollCount: 0,
      pollTimer: null,
      isFreeOrder: false,
      loading: false, // Changed to false so we can show the pending UI
      orderFetchPollCount: 0,
      orderFetchPollTimer: null,
      orderFetchError: false,
      isLoadingOrder: true, // New flag to track if we're still fetching the order
    }
  },
  computed: {
    TransactionStatus() {
      return TransactionStatus
    },
    repeatPurchaseUrl() {
      if (this.order && this.order.event && this.order.event.slug) {
        return `${frontendURL}/events/${this.order.event.slug}/register?lang=${i18next.language}`
      }
      if (this.slug) {
        return `${frontendURL}/events/${this.slug}/register?lang=${i18next.language}`
      }

      return landingURL
    },
    transactionStatusData() {
      if (this.transactionStatus === TransactionStatus.PENDING) {
        return {
          title: this.$t('transactionStatuses.PENDING'),
          description: this.$t('transactionStatusesDescription.PENDING'),
          backgroundColor: '#E6F1F8',
          color: '#0277BD',
          icon: '',
        }
      } else if (this.transactionStatus === TransactionStatus.SUCCESSFUL) {
        return {
          title: this.$t('transactionStatuses.SUCCESSFUL'),
          description: this.$t('transactionStatusesDescription.SUCCESSFUL'),
          backgroundColor: '#EEF6EF',
          color: '#0E7E1A',
          icon: 'check_circle',
        }
      } else if (this.transactionStatus === TransactionStatus.FAILED) {
        return {
          title: this.$t('transactionStatuses.FAILED'),
          description: this.$t('transactionStatusesDescription.FAILED'),
          backgroundColor: '#FBEBED',
          color: '#BD2A3C',
          icon: 'error',
        }
      }
    },
    customerEmail() {
      if (this.order && this.order.buyer && this.order.buyer.fields) {
        return this.order.buyer.fields['email'] || this.order.buyer.fields['Email'] || ''
      }

      return ''
    },
  },
  async mounted() {
    localStorage.removeItem('order')
    await this.fetchOrderWithPolling()
  },
  beforeDestroy() {
    this.stopPolling()
    this.stopOrderFetchPolling()
  },
  methods: {
    async fetchOrderWithPolling() {
      const uuid = this.$router.history.current.params.orderUUID

      try {
        this.order = await getOrderInfoByUUID(uuid)

        if (this.order) {
          // Successfully fetched order, stop polling and process it
          this.isLoadingOrder = false
          this.stopOrderFetchPolling()
          await this.processOrder()
        } else {
          // Order is null but no error - might be invalid UUID
          // After several retries with null response, it's probably a bad UUID
          if (this.orderFetchPollCount >= 5) {
            // If consistently null, redirect to 404
            this.$router.push('/404')
          } else {
            this.scheduleNextOrderFetch()
          }
        }
      } catch (error) {
        console.error('Error fetching order:', error)
        this.orderFetchError = true
        this.scheduleNextOrderFetch()
      }
    },

    scheduleNextOrderFetch() {
      this.orderFetchPollCount++

      let interval
      if (this.orderFetchPollCount < 10) {
        // First 10 calls: every 2 seconds
        interval = 2000
      } else if (this.orderFetchPollCount < 20) {
        // Next 10 calls: every 5 seconds
        interval = 5000
      } else if (this.orderFetchPollCount < 30) {
        // Next 10 calls: every 10 seconds
        interval = 10000
      } else {
        // After 30 attempts: every 30 seconds indefinitely
        interval = 30000
      }

      this.orderFetchPollTimer = setTimeout(() => {
        this.fetchOrderWithPolling()
      }, interval)
    },

    stopOrderFetchPolling() {
      if (this.orderFetchPollTimer) {
        clearTimeout(this.orderFetchPollTimer)
        this.orderFetchPollTimer = null
      }
    },

    async processOrder() {
      const lang = this.$router.history.current.params.lang || this.$route.query.lang

      this.isFreeOrder =
        this.order.paymentType === 'FREE' || ['EUR 0', 'EUR 0.00'].includes(this.order.price)
      const isFullKulturnaIskaznicaPayment = this.isFullKulturnaIskaznicaPayment()

      if (this.isFreeOrder || isFullKulturnaIskaznicaPayment) {
        this.transactionStatus = TransactionStatus.SUCCESSFUL
        this.transactionStatusPage = TransactionStatus.SUCCESSFUL

        this.$nextTick(async () => {
          await this.downloadTicket(this.order.uuid)
        })
      }

      this.hideTicketsForEvent3360 = !this.order.purchases.every(
        p => Number(p.orderItemId) === 9454,
      )

      i18next.changeLanguage(lang == 'en' ? 'en' : 'hr')
      const [{ slug, organization }, ads] = await Promise.all([
        getEventSettings(this.order.eventId),
        getEventAds(this.order.eventId),
      ])
      this.slug = slug
      this.organizationMetaPixelId = (organization && organization.metaPixelId) || null
      if (this.transactionStatusPage === TransactionStatus.SUCCESSFUL) {
        this.sendExpoGamersGoogleAdsConversion()
      }
      this.ads = (ads || []).map(a => {
        a.url = attachQueryParamsToUrl(a.url, {
          utm_source: 'CoreEvent',
          utm_campaign: 'Successful payment page',
          utm_content: `Event ${this.order.eventId}`,
        })

        return a
      })
      this.adsLoaded = true

      if (process.env.VUE_APP_ENVIRONMENT === 'production') {
        const [currency, value] = this.order.price.split(' ')
        const items = []
        this.order.purchases.map(p => {
          const existingItem = items.find(i => i.item_id === p.orderItemId)
          if (existingItem) {
            existingItem.quantity += 1
          } else {
            items.push({
              item_id: p.orderItemId,
              item_name: p.orderItem.name,
              price: Number(p.orderItem.price),
              quantity: 1,
            })
          }
        })
        //We are tracking purchases via server side events on the BE resolvers but for partners we send clientside events (so we can filter by organizer slug in the GTM)
        //await this.sendConversionApiEvent()
        window.dataLayer.push({
          event: 'purchase_for_partners',
          event_id: `${this.order.uuid}-purchase`,
          ecommerce: {
            transaction_id: this.order.uuid,
            order_id: this.order.id,
            value: Number(value),
            currency,
            organization_id: organization.id,
            organization_slug: organization.slug,
            purchase_organization_slug: organization.slug,
            event_id: this.order.eventId,
            event_slug: this.slug,
            purchase_event_slug: this.slug,
            items,
          },
        })
      }

      const { data = {} } = await this.getUser()
      const { me: user } = data || {}
      this.user = user

      if (this.isFreeOrder || this.transactionStatusPage === TransactionStatus.SUCCESSFUL) {
        await this.sendOrganizationPurchasePixel()
      }

      // Start polling for WsPay status if transaction is pending
      if (
        !this.isFreeOrder &&
        !isFullKulturnaIskaznicaPayment &&
        this.transactionStatusPage === TransactionStatus.PENDING
      ) {
        this.startPolling()
      }
    },

    isFullKulturnaIskaznicaPayment() {
      if (!this.order || this.order.paymentType !== 'KULTURNA_ISKAZNICA') {
        return false
      }

      const remainderAmount =
        this.order.kiData && this.order.kiData.remainderAmount
          ? Number(this.order.kiData.remainderAmount)
          : 0

      return !Number.isFinite(remainderAmount) || remainderAmount <= 0
    },

    async sendConversionApiEvent() {
      const fireKey = `purchaseFired-${this.order.uuid}`
      const email = this.order.buyer.fields.email

      if (!sessionStorage.getItem(fireKey)) {
        await sendFacebookPixelEvent({
          email,
          price: this.order.price,
          eventId: this.order.eventId,
          orderUuid: this.order.uuid,
        })
        sessionStorage.setItem(fireKey, '1')
      }
    },
    sendExpoGamersGoogleAdsConversion() {
      if (
        this.slug !== 'expo-gamers-2026-902b' ||
        !this.order ||
        !this.order.id
      ) {
        return
      }

      // Google Ads only with marketing cookie consent (public/js/consent.js).
      if (!window.ceConsent || !(window.ceConsent.get() || {}).marketing) {
        return
      }

      const fireKey = `expoGamersGoogleAdsConversionFired-${this.order.id}`

      if (sessionStorage.getItem(fireKey)) {
        return
      }

      const [currency = 'EUR', value = '1.0'] = (this.order.price || '').split(' ')
      const conversionEvent = {
        send_to: 'AW-16975274485/kdlqCOTC0bMaEPXDuJ4_',
        value: Number(value) || 1.0,
        currency,
        transaction_id: this.order.id,
      }

      if (typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', conversionEvent)
      } else {
        window.dataLayer = window.dataLayer || []
        window.gtag = function() {
          window.dataLayer.push(arguments)
        }
        window.gtag('event', 'conversion', conversionEvent)
      }
      sessionStorage.setItem(fireKey, '1')
    },
    async sendOrganizationPurchasePixel() {
      const fireKey = `organizationPurchaseFired-${this.order.uuid}`
      const email = this.order && this.order.buyer && this.order.buyer.fields ? this.order.buyer.fields.email : undefined

      if (sessionStorage.getItem(fireKey)) {
        return
      }

      try {
        const fired = await sendOrganizationFacebookPixelEvent({
          pixelId: this.organizationMetaPixelId,
          eventName: 'Purchase',
          price: this.order && this.order.price,
          orderUuid: this.order && this.order.uuid,
          eventId: this.order && this.order.eventId,
          email,
        })

        if (fired) {
          sessionStorage.setItem(fireKey, '1')
        }
      } catch (error) {
        console.warn('Failed to send organization Purchase pixel', error)
      }
    },
    goToPublicPage() {
      const lang = (i18next.language || 'hr').toLowerCase()
      window.open(`${landingURL}/events/${this.slug}/register?lang=${lang}`, '_self')
    },
    async downloadTicket(uuid) {
      try {
        // TODO: fix this function for Facebook browser (and other embedded browsers)
        const lang = this.$router.history.current.params.lang || this.$route.query.lang || 'hr'
        const ticketName = lang == 'en' ? 'CoreEvent-tickets.pdf' : 'CoreEvent-karte.pdf'

        const { data: ticket } = await axios.get(
          `${process.env.VUE_APP_SERVER_URL}/files/tickets/${uuid}?lang=${lang}`,
        )

        const byteString = window.atob(ticket.body)
        const arrayBuffer = new ArrayBuffer(byteString.length)
        const int8Array = new Uint8Array(arrayBuffer)
        for (let i = 0; i < byteString.length; i++) {
          int8Array[i] = byteString.charCodeAt(i)
        }
        const blob = new Blob([int8Array], { type: 'application/pdf' })
        const url = URL.createObjectURL(blob)
        const downloadLink = document.getElementById('download-ticket')
        downloadLink.href = url
        downloadLink.setAttribute('download', ticketName)
        this.ticketDownloaded = true
      } catch (e) {
        this.ticketError = true
      }
    },
    async getUser() {
      return this.$apollo.query({
        query: GET_LOGGED_USER,
      })
    },
    async checkWsPayStatus() {
      try {
        // Prepare the order items for the query
        const items = this.order.purchases.map(purchase => ({
          orderItemId: purchase.orderItemId,
          count: 1,
        }))

        // Group items by orderItemId to get the count
        const groupedItems = items.reduce((acc, item) => {
          const existing = acc.find(i => i.orderItemId === item.orderItemId)
          if (existing) {
            existing.count += 1
          } else {
            acc.push({ ...item })
          }

          return acc
        }, [])

        const { data } = await this.$apollo.query({
          query: CHECK_ORDER_WS_PAY_STATUS,
          variables: {
            order: {
              uuid: this.order.uuid,
              orderId: this.order.id,
              statusToken: getPaymentStatusToken(this.order.uuid),
              eventId: this.order.eventId,
              items: groupedItems,
            },
          },
          fetchPolicy: 'no-cache',
        })

        if (data && data.checkOrderWsPayStatus && data.checkOrderWsPayStatus.success) {
          // Payment successful
          this.transactionStatus = TransactionStatus.SUCCESSFUL
          this.sendExpoGamersGoogleAdsConversion()
          await this.sendOrganizationPurchasePixel()
          setTimeout(async () => {
            this.transactionStatusPage = TransactionStatus.SUCCESSFUL
            await this.downloadTicket(this.order.uuid)
          }, 2000)
          this.stopPolling()
        } else {
          // Continue polling
          this.scheduleNextPoll()
        }
      } catch (error) {
        console.error('Error checking WsPay status:', error)
        this.scheduleNextPoll()
      }
    },
    scheduleNextPoll() {
      this.pollCount++

      let interval
      if (this.pollCount < 20) {
        // First 20 calls: every 3 seconds
        interval = 3000
      } else if (this.pollCount < 30) {
        // Next 10 calls: every 5 seconds
        interval = 5000
      } else if (this.pollCount < 40) {
        // Next 10 calls: every 10 seconds
        interval = 10000
      } else {
        // A timeout is not proof of payment failure. Keep reconciling slowly
        // until the backend confirms a terminal state or this page closes.
        interval = 30000
      }

      this.pollTimer = setTimeout(() => {
        this.checkWsPayStatus()
      }, interval)
    },
    stopPolling() {
      if (this.pollTimer) {
        clearTimeout(this.pollTimer)
        this.pollTimer = null
      }
    },
    startPolling() {
      // Start polling immediately
      this.pollCount = 0
      this.checkWsPayStatus()
    },
  },
}
</script>

<style scoped>
.button-no-background {
  font-family: Raleway;
  font-style: normal;
  font-weight: bold;
  font-size: 16px;
  line-height: 120%;
  letter-spacing: -0.015em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #0277bd;
}

.mt-50 {
  margin-top: 50px;
}

.my-page-title {
  color: #333;
  font-variant-numeric: lining-nums proportional-nums;
  font-family: Raleway;
  font-size: 2.25rem;
  font-style: normal;
  font-weight: 800;
  line-height: 120%; /* 2.7rem */
  letter-spacing: -0.0675rem;
  margin-bottom: 1.5rem;
}

.app {
  background-image: url('/assets/background.png');
  background-position: right;
}

.registration-success-page-container {
  margin: auto;
  max-width: 1440px;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 6rem;
}

.registration-success-page {
  align-items: flex-start;
  max-width: 30rem;
  display: flex;
  flex-direction: column;
}

.info-image {
  width: 100%;
  height: 30rem;
}

.mt-220 {
  margin-top: 220px;
}

#buttons-container {
  margin: 2rem 0;
  max-width: 640px !important;
  display: flex;
  flex-direction: column;
}

@media screen and (max-width: 561px) {
  #buttons-container {
    flex-direction: column;
  }
}

@media screen and (max-width: 1187px) {
  .registration-success-page-container {
    padding: 0 1rem;
    justify-content: center;
    flex-direction: column;
  }

  .flex-column-reverse-mobile {
    flex-direction: column-reverse;
    gap: 2.5rem;
  }

  .registration-success-page {
    padding-bottom: 4rem;
  }

  .registration-success-page-container .shop-section {
    max-width: 38rem;
    width: calc(100% + 2.5rem);
    align-self: flex-end;
    margin-right: -2.5rem;
    border-radius: 1.5rem 0 0 1.5rem;
  }

  .shop-cards-section {
    overflow-x: scroll;
  }
}

.download-ticket {
  background-color: white;
  border: 2px solid #0277bd;
  color: #0277bd;
}

.v-btn--disabled {
  border: none;
}

#download-ticket .v-progress-circular {
  margin-left: 10px;
}

.main-button {
  font-size: 16px;
  height: 36px;
  font-family: Raleway, sans-serif;
  font-weight: bold;
  line-height: 1.5;
}

.shop-section {
  display: flex;
  flex-direction: column;
  padding: 1.5rem;
  background: #cee4f2;
  border-radius: 1.5rem;
}

.shop-section h4 {
  font-family: 'Raleway';
  font-weight: 600;
  font-size: 1.5rem;
  line-height: 120%;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #0277bd;
  padding-bottom: 1.5rem;
}

.shop-cards-section {
  display: flex;
}

.shop-card:nth-of-type(1),
.shop-card:nth-of-type(2) {
  margin-right: 1.5rem;
}

.shop-card {
  display: flex;
  flex-direction: column;
  border-radius: 1rem;
  background: #f0f6fa;
  align-items: flex-start;
  max-width: 10.3rem;
}

.shop-card-details {
  padding: 16px 18px 24px;
}

.shop-card-details h5 {
  color: #0277bd;
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 20px;
  line-height: 120%;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
}

.shop-card .logo-img {
  position: absolute;
  height: 2rem;
  width: auto;
  max-width: 4rem;
  background: white;
  border-radius: 1rem 0;
}

.shop-card .product-img {
  border-radius: 1rem 1rem 0 0;
  height: 10.3rem;
  width: 10.3rem;
}

.description {
  color: #333;
  font-family: 'Source Sans Pro';
  font-size: 1.25rem;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 1.875rem */
  margin: 0;
}

.transaction-status-container {
  display: flex;
  padding: 0.75rem 1.5rem 0.75rem 0.75rem;
  align-items: center;
  gap: 0.75rem;
  align-self: stretch;
  border-radius: 62.4375rem;
  margin: 1.5rem 0;
}

.transaction-status-title {
  margin: 0;
  font-family: 'Source Sans Pro';
  font-size: 1.125rem;
  font-style: normal;
  font-weight: 700;
  line-height: 110%; /* 1.2375rem */
}

.transaction-status-description {
  margin: 0;
  font-family: 'Source Sans Pro';
  font-size: 1.125rem;
  font-style: normal;
  font-weight: 400;
  line-height: 110%;
}
</style>
